import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { WEEKDAYS } from "@/lib/helpers";
import { AdminTitle } from "@/components/admin/AdminLayout";

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl bg-white border border-bej p-5 sm:p-6 space-y-4" data-testid={testid}>
    <h2 className="font-heading text-lg font-extrabold text-navy">{title}</h2>
    {children}
  </section>
);

const Text = ({ label, k, s, set, type = "text", hint }) => (
  <div>
    <label className="block text-sm font-bold text-navy mb-1.5">{label}</label>
    <Input type={type} value={s[k] ?? ""} onChange={(e) => set({ [k]: type === "number" ? Number(e.target.value) : e.target.value })} className="rounded-xl" data-testid={`settings-${k}`} />
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

function Hours({ s, set }) {
  const upd = (i, patch) => set({ working_hours: { ...s.working_hours, [i]: { ...s.working_hours[i], ...patch } } });
  return (
    <div className="space-y-2">
      {WEEKDAYS.map((d, i) => {
        const wh = s.working_hours[String(i)];
        return (
          <div key={d} className="flex flex-wrap items-center gap-3 rounded-xl bg-cream px-3 py-2" data-testid={`hours-row-${i}`}>
            <span className="w-24 font-bold text-navy">{d}</span>
            <Switch checked={wh.open} onCheckedChange={(v) => upd(String(i), { open: v })} data-testid={`hours-open-${i}`} />
            <span className="text-xs text-slate-500 w-12">{wh.open ? "Açık" : "Kapalı"}</span>
            {wh.open && (
              <>
                <Input type="time" value={wh.start} onChange={(e) => upd(String(i), { start: e.target.value })} className="w-28 rounded-xl" data-testid={`hours-start-${i}`} />
                <span>–</span>
                <Input type="time" value={wh.end} onChange={(e) => upd(String(i), { end: e.target.value })} className="w-28 rounded-xl" data-testid={`hours-end-${i}`} />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Holidays({ s, set }) {
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  const add = () => {
    if (!date) return;
    set({ holidays: [...s.holidays.filter((h) => h.date !== date), { date, label }].sort((a, b) => a.date.localeCompare(b.date)) });
    setDate("");
    setLabel("");
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto rounded-xl" data-testid="holiday-date-input" />
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Açıklama (ör. Bayram)" className="w-56 rounded-xl" data-testid="holiday-label-input" />
        <button type="button" onClick={add} className="inline-flex items-center gap-1 rounded-full bg-bej px-4 py-2 text-sm font-bold text-navy" data-testid="holiday-add-btn"><Plus className="w-4 h-4" /> Ekle</button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="holiday-list">
        {s.holidays.map((h) => (
          <span key={h.date} className="inline-flex items-center gap-2 rounded-full bg-cream border border-bej px-3 py-1 text-sm font-semibold text-navy">
            {h.date}{h.label && ` · ${h.label}`}
            <button type="button" onClick={() => set({ holidays: s.holidays.filter((x) => x.date !== h.date) })} aria-label="Kaldır" data-testid={`holiday-remove-${h.date}`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!s.holidays.length && <p className="text-sm text-slate-500">Tatil günü eklenmedi.</p>}
      </div>
    </div>
  );
}

function PasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Şifre güncellendi");
      setCur("");
      setNw("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Card title="Şifre değiştir" testid="settings-password-card">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
        <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Mevcut şifre" className="rounded-xl" data-testid="password-current" />
        <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Yeni şifre" className="rounded-xl" data-testid="password-new" />
        <p className="sm:col-span-2 text-xs text-slate-500">En az 10 karakter; büyük harf, küçük harf, rakam ve özel karakter içermeli.</p>
        <button type="submit" className="sm:col-span-2 justify-self-start rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white" data-testid="password-submit">Şifreyi güncelle</button>
      </form>
    </Card>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <p className="text-navy">Yükleniyor…</p>;
  const set = (patch) => setS((x) => ({ ...x, ...patch }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const p = { s, set };
  return (
    <div className="space-y-5 max-w-4xl" data-testid="admin-settings-page">
      <AdminTitle title="Ayarlar">
        <button onClick={save} disabled={busy} className="rounded-full bg-navy px-6 py-2.5 text-sm font-bold text-white hover:bg-navy-dark disabled:opacity-50" data-testid="settings-save-btn">
          {busy ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </AdminTitle>
      <Card title="Kurum bilgileri" testid="settings-info-card">
        <div className="grid sm:grid-cols-2 gap-4">
          <Text label="Kurum adı" k="academy_name" {...p} />
          <Text label="Slogan" k="tagline" {...p} />
          <Text label="Telefon (görünen)" k="phone" {...p} />
          <Text label="WhatsApp numarası" k="whatsapp_number" hint="Ülke koduyla, ör. 905011162344" {...p} />
          <Text label="Sitede görünen e-posta" k="public_email" {...p} />
          <Text label="Instagram adresi" k="instagram" {...p} />
          <Text label="Adres" k="address" {...p} />
          <Text label="Google Haritalar araması" k="maps_query" hint="Haritada aranacak adres veya işletme adı" {...p} />
          <Text label="Logo görsel adresi (URL)" k="logo_url" hint="Boş bırakılırsa varsayılan logo kullanılır" {...p} />
        </div>
      </Card>
      <Card title="Bildirimler" testid="settings-notify-card">
        <Text label="Yeni talep bildirim e-postası" k="notify_email" type="email" hint="Her yeni randevu talebinde bu adrese e-posta gönderilir." {...p} />
      </Card>
      <Card title="Randevu kuralları" testid="settings-rules-card">
        <div className="grid sm:grid-cols-3 gap-4">
          <Text label="Ders/slot süresi (dk)" k="slot_minutes" type="number" {...p} />
          <Text label="Kaç gün ileriye randevu" k="booking_days_ahead" type="number" {...p} />
          <Text label="IP başına saatlik talep sınırı" k="rate_limit_per_hour" type="number" {...p} />
        </div>
      </Card>
      <Card title="Çalışma saatleri (İstanbul saati)" testid="settings-hours-card"><Hours {...p} /></Card>
      <Card title="Tatil / kapalı günler" testid="settings-holidays-card"><Holidays {...p} /></Card>
      <PasswordCard />
    </div>
  );
}
